<!-- UX and CR are the sections whose scores differ per device, so only those get
     the platform toggle (Technical shares this component). -->
<app-audit-subbar
  [deviceAware]="sectionKey === 'ux' || sectionKey === 'cr'"
></app-audit-subbar>

<div class="audit-tab-body" style="position: relative">
  <div class="cat-wrap">
    <!-- No data -->
    <div class="cat-empty" *ngIf="!hasData && !isLoading">
      <p>No {{ label || "category" }} data available for this audit yet.</p>
    </div>

    <ng-container *ngIf="hasData">
      <!-- ── Page performance metrics (Technical) ── -->
      <div class="metric-section" *ngIf="techMetrics.length">
        <div class="cat-metric-header">
          <h3 class="cat-metric-title">Page performance metrics</h3>
          <span class="cat-metric-hint"
            >your score · value · vs last<span *ngIf="hasComp">
              · vs {{ compName }}</span
            ></span
          >
        </div>
        <div class="metric-grid">
          <div
            class="metric-card"
            *ngFor="let m of techMetrics"
            [matTooltip]="m.reason"
            matTooltipClass="multiline-tooltip"
            matTooltipPosition="above"
          >
            <div class="metric-top">
              <div class="metric-info">
                <div class="metric-name">{{ m.name }}</div>
                <div class="metric-value" *ngIf="m.value">{{ m.value }}</div>
              </div>
              <audit-ring
                [value]="m.score"
                [color]="m.color"
                [size]="58"
                [stroke]="6"
                [fontSize]="16"
              ></audit-ring>
            </div>
            <div class="metric-footer">
              <span
                class="metric-delta"
                *ngIf="m.deltaText"
                [style.color]="m.deltaColor"
              >
                {{ m.deltaArrow }} {{ m.deltaText }}
                <span class="metric-muted">vs last</span>
              </span>
              <span
                class="metric-comp"
                *ngIf="m.hasComp"
                [style.color]="m.compColor"
              >
                {{ m.compArrow }} {{ m.comp }} {{ compName }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Header card: score + benchmark + trend ── -->
      <div class="cat-head-card">
        <div class="cat-head-grid">
          <!-- Left: score + benchmark -->
          <div class="cat-head-left">
            <div class="cat-head-title">
              <h2>{{ label }}</h2>
              <span
                *ngIf="showStatusPill"
                class="cat-status"
                [style.color]="statusColor"
                [style.background]="statusBg"
                >{{ status }}</span
              >
            </div>
            <p class="cat-head-desc">
              {{ description
              }}<span *ngIf="hasComp">
                — benchmarked against {{ compName }}</span
              >.
            </p>

            <div class="cat-head-score">
              <span class="cat-head-num">{{ score }}</span>
              <span class="cat-head-unit">/100</span>
              <span
                class="cat-head-delta"
                *ngIf="delta !== null && delta !== 0"
                [style.color]="deltaColor"
              >
                {{ deltaArrow }} {{ deltaText }}
                <span class="cat-head-delta-sub">vs last period</span>
              </span>
            </div>

            <div class="cat-bench">
              <div class="cat-bench-head" *ngIf="hasComp">
                <span>Benchmark</span>
                <span [style.color]="leadColor">{{ leadText }}</span>
              </div>

              <div class="cat-bar-row" *ngFor="let b of benchBars">
                <span
                  class="cat-bar-label"
                  [class.cat-bar-label--muted]="b.isOptimal"
                  >{{ b.label }}</span
                >
                <div class="cat-bar-track">
                  <div
                    class="cat-bar-fill"
                    [style.width]="b.width"
                    [style.background]="b.color"
                  ></div>
                </div>
                <span class="cat-bar-val" [class.cat-bar-val--muted]="!b.isYou"
                  >{{ b.score
                  }}<small class="cat-bar-val-max">/100</small></span
                >
              </div>
            </div>
          </div>

          <!-- Right: score-over-time trend (biweekly audits with >1 data point
             only; hidden for one-time audits and single-point histories) -->
          <div class="cat-head-right" *ngIf="trendHasData">
            <div class="cat-trend-head">
              <span class="cat-trend-title">Score over time</span>
              <div class="wa-legend" *ngIf="trendLegend?.length">
                <button
                  type="button"
                  class="wa-legend-item"
                  *ngFor="let l of trendLegend"
                  [class.off]="!l.isYou && !l.visible"
                  [class.locked]="l.isYou"
                  (click)="toggleTrendLine(l)"
                >
                  <span
                    class="wa-legend-dash"
                    [style.background]="l.color"
                  ></span>
                  {{ l.label }}
                </button>
              </div>
            </div>
            <div class="cat-trend-body">
              <google-chart [data]="trendLineChart"></google-chart>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Sub-categories + selected detail ── -->
      <div class="cat-detail-grid">
        <!-- Sub-category list -->
        <div class="cat-sub-list">
          <div
            *ngFor="let s of subCards"
            class="cat-sub-card"
            [class.selected]="s.selected"
            (click)="selectSub(s)"
          >
            <div class="cat-sub-top">
              <span class="cat-sub-name">{{ s.name }}</span>
              <span class="cat-sub-right">
                <span class="cat-sub-cur"
                  >{{ s.cur }}<small class="cat-sub-cur-max">/100</small></span
                >
                <span
                  class="cat-sub-delta"
                  *ngIf="s.delta !== null && s.delta !== 0"
                  [style.color]="s.deltaColor"
                >
                  {{ s.deltaArrow }} {{ s.deltaText }}
                  <span class="cat-sub-muted">vs last</span>
                </span>
              </span>
            </div>
            <div class="cat-sub-bottom">
              <span class="cat-sub-desc">{{ s.description }}</span>
              <span
                class="cat-sub-comp"
                *ngIf="s.hasComp"
                [style.color]="s.compColor"
              >
                {{ s.compArrow }} {{ s.comp }}
                <span class="cat-sub-muted">{{ compName }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Selected detail -->
        <div class="cat-sel-card" *ngIf="selectedSub">
          <div class="cat-sel-head">
            <div>
              <h3>{{ selectedSub.name }}</h3>
              <p>{{ selectedSub.description }}</p>
            </div>
            <div class="cat-sel-stats">
              <div class="cat-sel-stat">
                <div class="cat-sel-stat-label">Your score</div>
                <div class="cat-sel-stat-val">
                  {{ selectedSub.score }}
                  <span
                    *ngIf="
                      selectedSub.delta !== null && selectedSub.delta !== 0
                    "
                    [style.color]="selectedSub.deltaColor"
                    >{{ selectedSub.deltaArrow }}
                    {{ selectedSub.deltaText }}</span
                  >
                </div>
              </div>
              <div class="cat-sel-stat" *ngIf="selectedSub.hasComp">
                <div class="cat-sel-stat-label">{{ selectedSub.compName }}</div>
                <div
                  class="cat-sel-stat-val"
                  [style.color]="selectedSub.compColor"
                >
                  {{ selectedSub.compArrow }} {{ selectedSub.comp }}
                </div>
              </div>
              <div class="cat-sel-stat" *ngIf="selectedSub.target !== null">
                <div class="cat-sel-stat-label">Optimal</div>
                <div class="cat-sel-stat-val cat-sel-stat-val--good">
                  {{ selectedSub.target }}
                </div>
              </div>
            </div>
          </div>

          <div class="cat-sel-divider">
            <ng-container *ngIf="selectedSub.hasComp"
              >Side-by-side: what each audit found for
            </ng-container>
            <ng-container *ngIf="!selectedSub.hasComp"
              >What the audit found for
            </ng-container>
            <b>{{ selectedSub.name }}</b>
          </div>

          <!-- Has competitor: your site vs competitor, side by side -->
          <div class="cat-sel-cols" *ngIf="selectedSub.hasComp">
            <!-- Your site -->
            <div class="cat-sel-col">
              <div class="cat-sel-col-head">
                <span
                  ><span class="cat-dot" [style.background]="accent"></span>Your
                  site</span
                >
                <span class="cat-sel-col-score">
                  {{ selectedSub.score }}
                  <span
                    *ngIf="
                      selectedSub.delta !== null && selectedSub.delta !== 0
                    "
                    [style.color]="selectedSub.deltaColor"
                    >{{ selectedSub.deltaArrow }}
                    {{ selectedSub.deltaText }}</span
                  >
                </span>
              </div>

              <div class="cat-sig-scroll">
                <div
                  class="cat-sig-label cat-sig-label--bad"
                  *ngIf="selectedSub.issues.length"
                >
                  OBSERVED ISSUES
                </div>
                <div
                  class="cat-sig cat-sig--bad"
                  *ngFor="let o of selectedSub.issues"
                >
                  <div class="cat-sig-title">{{ o.title }}</div>
                  <div class="cat-sig-desc">{{ o.desc }}</div>
                </div>

                <div
                  class="cat-sig-label cat-sig-label--good"
                  *ngIf="selectedSub.positives.length"
                >
                  POSITIVE SIGNALS
                </div>
                <div
                  class="cat-sig cat-sig--good"
                  *ngFor="let o of selectedSub.positives"
                >
                  <div class="cat-sig-title">{{ o.title }}</div>
                  <div class="cat-sig-desc">{{ o.desc }}</div>
                </div>
              </div>
            </div>

            <!-- Competitor -->
            <div class="cat-sel-col">
              <div class="cat-sel-col-head">
                <span
                  ><span class="cat-dot" [style.background]="compColor"></span
                  >{{ selectedSub.compName }}</span
                >
                <span
                  class="cat-sel-col-score"
                  [style.color]="selectedSub.compColor"
                  >{{ selectedSub.comp }}</span
                >
              </div>

              <div class="cat-sig-scroll">
                <div
                  class="cat-sig-label cat-sig-label--bad"
                  *ngIf="selectedSub.compIssues.length"
                >
                  OBSERVED ISSUES
                </div>
                <div
                  class="cat-sig cat-sig--bad"
                  *ngFor="let o of selectedSub.compIssues"
                >
                  <div class="cat-sig-title">{{ o.title }}</div>
                  <div class="cat-sig-desc">{{ o.desc }}</div>
                </div>

                <div
                  class="cat-sig-label cat-sig-label--good"
                  *ngIf="selectedSub.compPositives.length"
                >
                  POSITIVE SIGNALS
                </div>
                <div
                  class="cat-sig cat-sig--good"
                  *ngFor="let o of selectedSub.compPositives"
                >
                  <div class="cat-sig-title">{{ o.title }}</div>
                  <div class="cat-sig-desc">{{ o.desc }}</div>
                </div>

                <div
                  class="cat-sig-none"
                  *ngIf="
                    !selectedSub.compIssues.length &&
                    !selectedSub.compPositives.length
                  "
                >
                  No competitor breakdown available for this metric.
                </div>
              </div>
            </div>
          </div>

          <!-- No competitor: split Observed Issues / Positive Signals 50/50 (#16) -->
          <div class="cat-sel-cols" *ngIf="!selectedSub.hasComp">
            <div class="cat-sel-col">
              <div class="cat-sel-col-head">
                <span
                  ><span class="cat-dot" [style.background]="red"></span
                  >Observed issues</span
                >
              </div>
              <div class="cat-sig-scroll">
                <div
                  class="cat-sig cat-sig--bad"
                  *ngFor="let o of selectedSub.issues"
                >
                  <div class="cat-sig-title">{{ o.title }}</div>
                  <div class="cat-sig-desc">{{ o.desc }}</div>
                </div>
                <div class="cat-sig-none" *ngIf="!selectedSub.issues.length">
                  No issues found for this metric.
                </div>
              </div>
            </div>

            <div class="cat-sel-col">
              <div class="cat-sel-col-head">
                <span
                  ><span class="cat-dot" [style.background]="green"></span
                  >Positive signals</span
                >
              </div>
              <div class="cat-sig-scroll">
                <div
                  class="cat-sig cat-sig--good"
                  *ngFor="let o of selectedSub.positives"
                >
                  <div class="cat-sig-title">{{ o.title }}</div>
                  <div class="cat-sig-desc">{{ o.desc }}</div>
                </div>
                <div class="cat-sig-none" *ngIf="!selectedSub.positives.length">
                  No positive signals for this metric.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </div>

  <!-- Competitor switch overlay (scoped to the content, below the sub-bar) -->
  <div class="loading-data-overlay" *ngIf="competitorSwitching$ | async">
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  </div>

  <div class="loading-data-overlay" *ngIf="isLoading">
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  </div>
</div>
